import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';

import { useQuery, useSubscription } from '@apollo/client';
import NotesQuery from 'services/graphQL/query/NotesQuery';
import NotesSubscription from 'services/graphQL/subscription/NotesSubscription';

import { makeStyles, useMediaQuery } from '@material-ui/core';
import Table from 'components/widgets/Table';

import DateUtils, { DateFormat } from 'lib/DateUtils';
import ModalUtils from 'utils/ModalUtils';
import { FetchPolicy, MEDIA_QUERY } from 'utils/enum/Core';
import { CodeType } from 'utils/enum/NotesEnum';
import MobileListView from 'components/widgets/MobileListView';

const useStyles = makeStyles({
    main: {
        flex: 1,
        padding: '20px 10px',
    },
    column: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    descriptionCol: {
        whiteSpace: 'inherit !important',
    },
});
const ACTION_TYPES = {
    SET_RECORDS: 'setRecords',
    ADD_RECORD: 'addRecord',
};
const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPES.SET_RECORDS:
        return action.payload;
    case ACTION_TYPES.ADD_RECORD:
        return update(state, { $unshift: [action.payload] });
    default:
        return [];
    }
};

const HistoryTab = ({ dealId }) => {
    const classes = useStyles();
    const [records, dispatch] = useReducer(reducer, []);
    const { data, error, loading } = useQuery(NotesQuery.GET_DEAL_NOTES, {
        variables: { dealId },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });
    const { data: subscriptionData } = useSubscription(NotesSubscription.NOTE_ADDED, {
        variables: {
            codeType: CodeType.DEALS,
            code: dealId,
        },
        shouldResubscribe: true,
    });

    useEffect(() => {
        if (!loading) {
            dispatch({
                type: ACTION_TYPES.SET_RECORDS,
                payload: data?.getDealNotes,
            });
        }
        if (error) {
            ModalUtils.errorMessage(null, error?.message);
        }
    }, [data, error, loading]);
    useEffect(() => {
        if (subscriptionData) {
            dispatch({
                type: ACTION_TYPES.ADD_RECORD,
                payload: subscriptionData.noteAdded,
            });
        }
    }, [subscriptionData]);

    const columns = [
        {
            Header: 'User',
            accessor: 'noteUser',
            maxWidth: 110,
        },
        {
            Header: 'Date',
            accessor: 'noteDate',
            maxWidth: 200,
            className: classes.column,
            renderCustomCell: true,
            Cell: (record) => DateUtils.format(record.value, DateFormat.DATETIME_WITHOUT_SECONDS),
        },
        {
            Header: 'Description',
            accessor: 'noteText',
            className: classes.descriptionCol,
        },
        {
            Header: 'Platform',
            accessor: 'client',
            className: classes.column,
            maxWidth: 130,
        },
    ];

    const isMobile = useMediaQuery(`(max-width:${MEDIA_QUERY.W_430}px)`);

    const renderMobileList = () => (
        <MobileListView
            columns={columns}
            records={records}
            onClickRecord={() => null}
            onSelectRow={() => null}
        />
    );

    const renderDesktopTable = () => (
        <Table
            loading={loading}
            columns={columns}
            data={records}
            rowSelected
        />
    );

    return (
        <div className={classes.main}>
            {isMobile ? renderMobileList() : renderDesktopTable()}
        </div>
    );
};

HistoryTab.propTypes = {
    dealId: PropTypes.number.isRequired,
};

export default HistoryTab;
